<template>
  <config-base class="opc-lm-edit" ref="baseRef" :on="$listeners">
    <template #config>
      <bought-license :type="planTypes.LARGE_MEETING" />
      <capacity-config-list ref="configListRef" :config-plan-type="planType" :config-capacity-key="capacityKey" :capacity-unit-i18n-key="capacityUnitI18nKey" :showMonthlyPriceFromCycle="true" :current-license="currentEditPlan && currentEditPlan.cartInfo && currentEditPlan.cartInfo.selected_number"></capacity-config-list>
      <div
        style="margin-top: 24px;"
        v-if="addMoreVisible"
        v-html="$t('billing.large_meeting.add_more_plan_link_capacity', [title, addFlowLink])"
        v-intercept-link="handleAddMore"
      >
      </div>
      <preview-payment class="opc-lm-edit__preview-payment" />
    </template>
  </config-base>
</template>

<script>
import _, {get, map} from "lodash";
import {mapGetters} from "vuex";
import {
  BILLING_CYCLE,
  sortBillingCycle,
  planTypes,
} from '@/common/constants';
import planCommon from '@/plans/LARGE_MEETING/mixin';
import {
  computedCapacityLargePlans,
  computedLargeMeetingPlans,
} from "@/common/service/computers/plans/large-meeting";
import {getPlanTotalLicense} from "@/common/service/validate";
import { pageConfig } from '@/common/service/page-config';
import {getNeedFilterCapacityList} from '@/common/product';
import plansMixin from "@/plans/mx/plan";
import ConfigList from "@/components/capacity/config-list.vue";
import ConfigBase from "@/plans/LARGE_MEETING/config/base.vue";
import addonMixin from "@/plans/mx/addon";
import replenishMixin from "@/components/capacity/mx/replenish";
import standardPriceMixin from '@/components/capacity/mx/standard-price';
import capacityPlanMixin from '@/components/capacity/mx/capacity-plan';
import PreviewPayment from '@/views/checkout/payment/preview-payment';
import { plansof } from '@/utils/chain';
import BoughtLicense from '@/views/config/shopping-cart/prorated/bought-license.vue';
import { jumpToTargetPageForDiffType } from '@/utils/modal-utils';

const CapacityConfigList = {
  name: 'CapacityConfigList',
  extends: ConfigList,
  mixins: [planCommon, addonMixin, replenishMixin, standardPriceMixin, capacityPlanMixin],
  data () {
    return {
      title: this.$t('billing.large_meeting.title'),
      msgInstance: null,
    }
  },
  computed: {
    ...mapGetters(['getCurrentMeetingPlans', 'getCurrentBasePlanInfo', 'boughtPlans', 'getBoughtMeetingPlans', 'basicConfig', 'currentEditPlan']),
    boughtCurrentPlans() {
      return computedLargeMeetingPlans(this.boughtPlans || [])
    },
    planList() {
      return _.chain(this.planMapFlat)
        .filter((p) => {
        const isCurrentPlanType = p.type === this.planType;
        const isSupportCapacity = this.supportCapacity.includes(p[this.capacityKey]);
        const needFilterCapacity = getNeedFilterCapacityList(this.getCurrentBasePlanInfo?.plans).includes(p[this.capacityKey])
        const isBoughtCapacity = map(this.boughtCurrentPlans, this.capacityKey).includes(p[this.capacityKey]);
        const otherBoughtCapacity = isBoughtCapacity && this.currentEditPlan?.capacity !== p[this.capacityKey]
        const selectablePlanList = isCurrentPlanType && isSupportCapacity && !needFilterCapacity && !otherBoughtCapacity
        return this.isBulkLicenseAccount? (selectablePlanList && isBoughtCapacity) : selectablePlanList
      })
      .filter((p) => {
        const currentCapacity = _.get(this.currentEditPlan, [this.capacityKey]);

        // with cancellation coupon campaign case and current edit plan is pending status, do not allow switch to other capacity
        if (this.fromCancelCampaignWithPendingCase) {
          return p.capacity === currentCapacity;
        }

        // Can only be upgraded to a higher capacity
        if (this.disableDownSell && currentCapacity) {
          return p.capacity >= currentCapacity;
        }

        return p;
      })
      .value();
    },
    currentBasePlans() {
      return this.getCurrentMeetingPlans.length ? this.getCurrentMeetingPlans : this.getCurrentBasePlanInfo?.plans;
    },
    boughtCapacityList() {
      return [...new Set(map(this.boughtCurrentPlans, this.capacityKey))].filter(capacity => this.supportCapacity.includes(capacity));
    },
    isBulkLicenseAccount() {
      return this.basicConfig?.isBulkLicenseAccount
    },
    disableDownSell() {
      return this.basicConfig?.downSellData?.disableDownSell
    },
    mx_planLicenseCount() {
      return this.mx_totalCapacityLicenseCount(this.visibleCapacityList[0], this.capacityKey);
    },
    editTotalLimitDesc() {
      const basePlanNameKey = get(this.currentBasePlans, [0, 'parentPlanNameKey']) || get(this.currentBasePlans, [0, 'planNameKey']) || get(this.currentBasePlans, [0, 'typeNameKey'])
      return this.$t('billing.addon_license_limit_tips', [this.title, this.$t(basePlanNameKey)])
    },
    addCapacityBtnVisible() {
      return false;
    },
    mx_showSelectCapacityDropdown() {
      return this.allCapacityOrderList.length > 1;
    },
    mx_licenseTips() {
      return this.totalLimitDesc;
    }
  },
  methods: {
    afterSwitchCapacityHook(oldSelectedPlan, newPlan) {
      if (!this.mx_showSelectCapacityDropdown) return
      this.mx_checkCapacityChanged(newPlan)
    },
    mx_getBillingCycleList(capacity) {
      const { capacityMap } = this;
      const capacityPlanList = capacityMap[capacity] || [];
      const monthlyLargePlans = plansof(this.boughtCurrentPlans).monthly().get();
      const annualLargePlans = plansof(this.boughtCurrentPlans).annual().get();
      // current edit category one month plan
      const oneMonthLargePlans = plansof(this.boughtCurrentPlans).one_month().category(pageConfig?.subPlan).get();

      const hideOneMonth = (monthlyLargePlans.length || annualLargePlans.length) && oneMonthLargePlans.length === 0;

      const currentMonthlyPlans4Capacity = plansof(monthlyLargePlans).capacity(capacity).get();
      const currentAnnualPlans4Capacity = plansof(annualLargePlans).capacity(capacity).get();

      const monthlyOptions = this.getBillingCycleOptions(currentMonthlyPlans4Capacity, undefined, oneMonthLargePlans);
      const annualOptions = this.getBillingCycleOptions(currentAnnualPlans4Capacity, {
        disabled: this.mx_annualPlanDisabled.disabled,
        reason: this.mx_annualPlanDisabled.reason,
      }, oneMonthLargePlans);

      return sortBillingCycle(
        [
          {
            billingCycle: BILLING_CYCLE.ONE_MONTH,
            plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.ONE_MONTH),
            hidden: hideOneMonth,
          },
          {
            billingCycle: BILLING_CYCLE.MONTHLY,
            plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.MONTHLY),
            disabled: monthlyOptions.disabled,
            tooltips: monthlyOptions.reason,
          },
          {
            billingCycle: BILLING_CYCLE.ANNUAL,
            plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.ANNUAL),
            disabled: annualOptions.disabled,
            tooltips: annualOptions.reason,
          },
          {
            billingCycle: BILLING_CYCLE.QUARTERLY,
            plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.QUARTERLY),
          },
          {
            billingCycle: BILLING_CYCLE.TWO_YEARS,
            plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.TWO_YEARS),
          },
          {
            billingCycle: BILLING_CYCLE.THREE_YEARS,
            plan: capacityPlanList.find(p => p.billing_cycle === BILLING_CYCLE.THREE_YEARS),
          },
        ].filter(p => p.plan && !p.hidden)
      );
    },
    initVisibleCapacityList() {
      if (this.selectedPlanList?.length) {
        // ZOOM-553979
        this.visibleCapacityList = map(this.selectedPlanList.filter(p => p.category === pageConfig?.subPlan || this.mx_showSelectCapacityDropdown), this.capacityKey);
      } else {
        this.visibleCapacityList = this.allCapacityOrderList.slice(0, 1);
      }
    },
    handleLicenseChange(license) {
      this.mx_validDownSellLicense(license)
      if (license >= this.mx_getMaxHosts() && !this.mx_canAutoAdd && this.mx_getMaxHosts() !== 999) {
        this.mx_licenseAlert = this.editTotalLimitDesc
      } else if (this.mx_downSellLicenseErrorMessage) {
        this.mx_licenseAlert = this.mx_downSellLicenseErrorMessage
      } else {
        this.mx_licenseAlert = ''
      }
    },
    getCurrentCapacityList() {
      return this.allCapacityOrderList
    },
    showMessage(message) {
      this.msgInstance && this.msgInstance.close();
      this.msgInstance = this.$message({
        type: 'info',
        duration: 8000,
        message: message,
        showClose: true
      })
    },
    mx_getMinHosts(capacity) {
      // non standard price
      if (this.checkCurrentCapacityIsNonStandardPrice(capacity)) {
        return this.currentBoughtLicense;
      }

      // for edit
      if (this.disableDownSell) {
        return getPlanTotalLicense([this.currentEditPlan]) || 1;
      }

      // for pending plan with cancellation coupon
      if (this.fromCancelCampaignWithPendingCase) {
        return this.currentBoughtLicense;
      }

      return 1
    },
    mx_getMaxHosts() {
      return this.mx_canAutoAdd ? 999 : (getPlanTotalLicense(this.currentBasePlans || []) || 999);
    },
    mx_getDecreaseTips(capacity) {
      const currentCapacityPlanCount = getPlanTotalLicense(computedCapacityLargePlans(this.selectedPlanList || [], capacity));
      const currentIsEqualPrev = currentCapacityPlanCount > 1 && currentCapacityPlanCount === this.mx_getMinHosts(capacity);

      if (this.checkCurrentCapacityIsNonStandardPrice(capacity) && currentIsEqualPrev) {
        return this.$t('billing.planform.meeting_license_custom_pricing');
      }

      // for pending plan with cancellation coupon
      if (this.fromCancelCampaignWithPendingCase && currentIsEqualPrev) {
        return this.$t('billing.coupon.not_switch_plan_pending_plans');
      }

      if (currentIsEqualPrev) {
        return this.$t('billing.error.can_not_bought_plan_tips')
      }
      return ''
    },
    mx_onAddedBasePlan(basePlanName) {
      this.showMessage(
        this.$t('billing.addon_auto_add_tips', [this.title, basePlanName]),
      );
    },
    mx_onRemovedBasePlan(basePlanName) {
      this.showMessage(
        this.$t('billing.addon_auto_remove_tips', [this.title, basePlanName]),
      );
    }
  }
}
export default {
  mixins: [plansMixin, planCommon],
  components: { PreviewPayment, ConfigBase, CapacityConfigList, BoughtLicense },
  data() {
    return { isMounted: false, planTypes: planTypes }
  },
  computed: {
    ...mapGetters(['isDisableAdd2Cart']),
    isBulkLicenseAccount() {
      return this.basicConfig?.isBulkLicenseAccount
    },
    addFlowLink() {
      if (!this.isMounted) return;
      const { configListRef } = this.$refs;
      const basePlan = configListRef?.currentBasePlans[0] || {}
      const basePlanCount = getPlanTotalLicense(configListRef?.currentBasePlans || [])
      return `/opc/buy/add?plan=${basePlan?.category}&subPlan=newsubs&from_sub=large&type=add&majorPlanHost=${basePlanCount}`
    },
    addMoreVisible() {
      if (!this.isMounted) return;
      const { configListRef } = this.$refs;
      return !this.isDisableAdd2Cart && configListRef?.allCapacityOrderList?.length > 1 && !this.isBulkLicenseAccount;
    },
  },
  methods: {
    handleAddMore(event) {
      jumpToTargetPageForDiffType(event?.target?.href)
    }
  },
  mounted() {
    // Make $refs reactive in computed
    this.isMounted = true;
  }
};
</script>

<style lang="scss">
.opc-lm-edit {
  &__preview-payment {
    margin-top: 24px;
  }
}
</style>
